import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../../services/footballApi';

interface League {
  id: number;
  name: string;
  long_name?: string;
  abbreviation?: string;
  timeline?: string;
  format?: string;
  start_date?: string;
  end_date?: string;
  is_visible?: boolean;
  is_trophy?: boolean;
  created_at?: string;
  updated_at?: string;
}

const API_URL = '/leagues/';

const FootballLeagues: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchLeagues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(API_URL);
      setLeagues(res.data);
    } catch (err: any) {
      setError('Failed to fetch leagues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  const handleEdit = (id: number) => {
    navigate(`/football/leagues/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this league?')) return;
    try {
      await api.delete(`${API_URL}${id}/`);
      fetchLeagues();
    } catch (err) {
      alert('Failed to delete league');
    }
  };

  const handleCreate = () => {
    navigate('/football/leagues/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'long_name', headerName: 'Long Name', flex: 1, minWidth: 150 },
    { field: 'abbreviation', headerName: 'Abbr.', width: 100 },
    { field: 'timeline', headerName: 'Timeline', width: 120 },
    { field: 'format', headerName: 'Format', width: 120 },
    { field: 'start_date', headerName: 'Start Date', width: 140, valueGetter: (value) => value ? new Date(value).toLocaleDateString() : '' },
    { field: 'end_date', headerName: 'End Date', width: 140, valueGetter: (value) => value ? new Date(value).toLocaleDateString() : '' },
    { field: 'is_visible', headerName: 'Visible', width: 90, type: 'boolean' },
    { field: 'is_trophy', headerName: 'Trophy', width: 90, type: 'boolean' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: League }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Leagues</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add League
        </Button>
      </Stack>
      <DataGrid
        rows={leagues}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballLeagues; 